import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronDown, Copy, Search, X } from 'lucide-react'
import { useTRPC } from '@/lib/trpc'
import type { Holding, RouterOutputs } from '@/lib/api-types'
import { CORE_TOKEN_LOGOS, EXCHANGE_COIN_DEFAULT_TOKENS, SOL_MINT, SOLANA_DEFAULT_TOKENS, tokenLogo } from '@/lib/default-tokens'
import { formatPrice, formatTokenAmount, formatUsd, shortAddress } from '@/lib/format'
import { NETWORK_SHORT_LABELS, chainBadgeOf, exchangeCoinOf, isAccountHolding } from '@/lib/networks'
import { TokenIcon } from './holdings-table'
import { Spinner } from './spinner'
import type { Chain } from '../../../src/constants/chains'

const SOLANA = 'solana' as Chain
type SearchResult = RouterOutputs['token']['search'][number]

export { holdingToPicked, SOL_TOKEN, type PickedToken } from '@/lib/picked-token'
import { holdingToPicked, SOL_TOKEN, type PickedToken } from '@/lib/picked-token'

/** The chain's core tokens, with balances filled in from holdings; with `exchangeCoins`, the exchanges' ETH and BNB too (the Convert side, Nov 3 2026). */
export function coreTokens(holdings: Holding[], exchangeCoins = false): PickedToken[] {
  const held = holdings.filter(isAccountHolding).map(holdingToPicked)
  const sol = held.find((t) => t.isNative) ?? SOL_TOKEN
  const defaults = exchangeCoins ? [...SOLANA_DEFAULT_TOKENS, ...EXCHANGE_COIN_DEFAULT_TOKENS] : SOLANA_DEFAULT_TOKENS
  const rest = defaults.map((d) => held.find((t) => t.address === d.address) ?? { ...d, isNative: false })
  return [sol, ...rest]
}

export function TokenPicker({
  value,
  onChange,
  holdings,
  allowSearch,
  heldOnly = false,
  showSymbol = false,
  onlyHoldings = false,
  exchangeCoins = false,
  tokens,
  expand,
}: {
  value: PickedToken | null
  onChange: (token: PickedToken) => void
  holdings: Holding[]
  /** search any Solana token (receive side) */
  allowSearch: boolean
  /** only tokens with a balance can be picked (pay side) */
  heldOnly?: boolean
  /** show the ticker next to the logo; the swap keeps logos only */
  showSymbol?: boolean
  /** list just the given holdings, without the core tokens section (a strategy's sell side) */
  onlyHoldings?: boolean
  /** list the exchanges' ETH and BNB among the core coins, at zero balance when not held (the swap dialog's Convert side) */
  exchangeCoins?: boolean
  /** list exactly these, in this order, instead of the holdings (the deposit dialog's coins, Nov 4 2026) */
  tokens?: PickedToken[]
  /** the entries one listing stands for: the exchanges' ETH once per chain it moves on (the withdraw dialog, Nov 4 2026) */
  expand?: (token: PickedToken) => PickedToken[]
}) {
  const trpc = useTRPC()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  // The panel is portaled above the modal (which clips overflow) and placed
  // under the trigger, right-aligned, with a height that fits the viewport.
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({})
  const phone = useIsPhone()
  useLayoutEffect(() => {
    if (!open || !ref.current) return
    if (phone) {
      // Phones get a full-screen sheet pinned to the visual viewport: when the
      // keyboard opens, the list shrinks in place instead of the panel jumping.
      const vv = window.visualViewport
      const place = () =>
        setPanelStyle(vv ? { position: 'fixed', top: vv.offsetTop, left: vv.offsetLeft, width: vv.width, height: vv.height } : { position: 'fixed', inset: 0 })
      place()
      vv?.addEventListener('resize', place)
      vv?.addEventListener('scroll', place)
      return () => {
        vv?.removeEventListener('resize', place)
        vv?.removeEventListener('scroll', place)
      }
    }
    const place = () => {
      const r = ref.current!.getBoundingClientRect()
      const width = 320
      const left = Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8))
      const top = r.bottom + 8
      setPanelStyle({ position: 'fixed', top, left, width, maxHeight: Math.max(160, window.innerHeight - top - 16) })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, phone])
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300)
    return () => clearTimeout(t)
  }, [query])
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (ref.current?.contains(t) || panelRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])
  const search = useQuery({
    ...trpc.token.search.queryOptions({ query: debounced }),
    enabled: allowSearch && open && debounced.length >= 2,
    staleTime: 60_000,
  })

  // one list: everything held plus the core tokens, highest value first (John, Oct 29 2026)
  const core = onlyHoldings ? [] : coreTokens(holdings, exchangeCoins)
  const held = holdings
    // the core list already carries SOL (by the wrapped mint); the wallet's native row must not be added again
    .filter((t) => isAccountHolding(t) && Number(t.balance) > 0 && !(t.isNative && core.some((c) => c.isNative)) && !core.some((c) => c.address === t.address))
    .map(holdingToPicked)
  const valueOf = (t: PickedToken) => (t.balance ?? 0) * (t.priceUsd ?? 0)
  const listed = (tokens ?? [...core, ...held].sort((a, b) => valueOf(b) - valueOf(a) || (b.balance ?? 0) - (a.balance ?? 0))).flatMap((t) => (expand ? expand(t) : [t]))
  const pickable = (t: PickedToken) => !heldOnly || (t.balance ?? 0) > 0
  const pick = (t: PickedToken) => {
    onChange(t)
    setOpen(false)
    setQuery('')
  }
  const fromSearch = (r: SearchResult): PickedToken => ({
    address: r.address,
    symbol: (r.symbol ?? '').toUpperCase(),
    name: r.name ?? '',
    imageUrl: tokenLogo(r.address, r.imageUrl),
    priceUsd: r.priceUsd,
    marketCapUsd: r.marketCapUsd,
    isNative: false,
  })
  // same mint can come back twice from different sources
  const uniqueResults = (results: SearchResult[]) => {
    const seen = new Set<string>()
    return results.filter((r) => r.chain === 'solana' && !seen.has(r.address) && seen.add(r.address))
  }

  return (
    <div className="relative" ref={ref}>
      {/* logo only, John's call: the symbol lives in the accessible name and the tooltip */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={value ? `${value.symbol}${value.network ? ` ${NETWORK_SHORT_LABELS[value.network]}` : ''}` : undefined}
        aria-label={value ? `${value.symbol}${value.network ? ` ${NETWORK_SHORT_LABELS[value.network]}` : ''}` : 'Select token'}
        className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-control bg-surface-2 px-2.5 text-sm font-semibold transition-colors hover:bg-surface-3"
      >
        {value ? (
          <>
            <TokenIcon symbol={value.symbol} imageUrl={value.imageUrl} address={value.address} size="size-6" badge={chainBadgeOf(value)} />
            {showSymbol ? value.symbol : null}
            {/* the chain, for a coin listed once per chain: "ETH Base" (John, Nov 4 2026) */}
            {showSymbol && value.network ? <span className="font-normal text-muted">{NETWORK_SHORT_LABELS[value.network]}</span> : null}
          </>
        ) : (
          'Select token'
        )}
        <ChevronDown className="size-4 shrink-0 text-muted" />
      </button>
      {open ? createPortal(
        <div
          ref={panelRef}
          data-popover
          style={panelStyle}
          className={`z-[70] flex flex-col overflow-hidden bg-surface ${phone ? '' : 'rounded-card border border-line shadow-2xl'}`}
        >
          {phone ? (
            <div className="flex items-center justify-between border-b border-line px-4 py-3" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
              <span className="font-semibold">Select token</span>
              <button type="button" className="btn btn-ghost !p-1" onClick={() => setOpen(false)} aria-label="Close">
                <X className="size-5" />
              </button>
            </div>
          ) : null}
          {/* The keyboard waits for a tap on phones so the list is readable first; desktop focuses straight away. */}
          {allowSearch ? (
            <div className="flex items-center gap-2 border-b border-line px-3 py-2">
              <Search className="size-4 shrink-0 text-muted" />
              <input
                autoFocus={!phone}
                className="w-full bg-transparent py-1 text-base sm:text-sm outline-none"
                placeholder="Search name, symbol or mint"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="search"
              />
              {search.isFetching ? <Spinner className="size-4" /> : null}
            </div>
          ) : null}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1">
            {debounced.length >= 2 && allowSearch ? (
              uniqueResults(search.data ?? []).length === 0 ? (
                <p className="px-3 py-3 text-sm text-muted">{search.isFetching ? 'Searching…' : 'No Solana tokens found.'}</p>
              ) : (
                uniqueResults(search.data ?? []).map((r) => (
                  <TokenRow key={r.address} token={fromSearch(r)} onPick={pick} />
                ))
              )
            ) : (
              <>
                {listed.map((t) => (
                  <TokenRow key={`${t.address}:${t.network ?? ''}`} token={t} onPick={pick} disabled={!pickable(t)} />
                ))}
                {listed.length === 0 ? <p className="px-3 py-3 text-sm text-muted">Nothing to pick yet.</p> : null}
              </>
            )}
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  )
}

/** Below Tailwind's `sm` breakpoint the picker is a sheet rather than a popover. */
function useIsPhone() {
  const [phone, setPhone] = useState(() => window.matchMedia('(max-width: 639px)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)')
    const update = () => setPhone(mq.matches)
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])
  return phone
}

function TokenRow({ token, onPick, disabled = false }: { token: PickedToken; onPick: (t: PickedToken) => void; disabled?: boolean }) {
  const [copied, setCopied] = useState(false)
  async function copyAddress(e: React.MouseEvent) {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(token.address)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // clipboard blocked
    }
  }
  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={() => { if (!disabled) onPick(token) }}
      onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onPick(token) } }}
      className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm ${disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:bg-surface-2'}`}
    >
      <span className="flex min-w-0 items-center gap-2">
        <TokenIcon symbol={token.symbol} imageUrl={token.imageUrl} address={token.address} size="size-6" badge={chainBadgeOf(token)} />
        <span className="min-w-0">
          <span className="font-medium">{token.symbol}</span>
          <span className="ml-1 truncate text-xs text-muted">{token.name}</span>
          {/* the exchanges' ETH and BNB have no mint to show or copy, and no words in its place (John, Nov 4 2026); a coin on another chain than Solana has none to show either */}
          {exchangeCoinOf(token) === 'ETH' || exchangeCoinOf(token) === 'BNB' || (token.network && token.network !== 'solana') ? null : (
            <span className="flex items-center gap-1 font-mono text-xs text-muted">
              {token.isNative ? 'Native SOL' : shortAddress(token.address, 4)}
              {!token.isNative ? (
                <button type="button" onClick={copyAddress} title="Copy contract address" className="rounded p-0.5 hover:text-text">
                  {copied ? <Check className="size-3 text-accent" /> : <Copy className="size-3" />}
                </button>
              ) : null}
            </span>
          )}
        </span>
      </span>
      <span className="shrink-0 text-right text-xs text-muted">
        {token.balance !== undefined ? (
          // a held token: how much, and what that is worth (John, Oct 31 2026)
          <>
            <div className="text-text">{formatTokenAmount(token.balance, token.symbol)}</div>
            {token.balance > 0 && token.priceUsd ? <div>{formatUsd(token.balance * token.priceUsd)}</div> : null}
          </>
        ) : (
          // a searched token: its price, then its market cap
          <>
            {token.priceUsd ? <div className="text-text">{formatPrice(token.priceUsd)}</div> : null}
            {token.marketCapUsd ? <div>MC {formatUsd(token.marketCapUsd, true)}</div> : null}
          </>
        )}
      </span>
    </div>
  )
}
